/**
 * Which browser identity this tab acts as. Several people and bots can share one browser (one VM, one OS user):
 * each login gets its own cookie slot on the server (vp_sid_<slot> / vp_csrf_<slot>). The tab keeps its slot in
 * sessionStorage (per tab, survives reloads) and sends it as `X-VP-Slot`; URLs that cannot carry headers (media,
 * EventSource, Markdown downloads) carry `?slot=` instead. The slot is a name, never a secret. A tab without a slot
 * acts as the most recent login in this browser and pins it on first load, so a later login in another tab does not
 * change who this tab is.
 */
const KEY = "vp.slot";
const EVENT = "vp:identity";
export const LEGACY_SLOT = "default";

export function getSlot(): string | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Bind this tab to a slot (null: follow the most recent login). Fires a `vp:identity` window event. */
export function setSlot(slot: string | null): void {
  if (getSlot() === slot) return;
  try {
    if (slot) window.sessionStorage.setItem(KEY, slot);
    else window.sessionStorage.removeItem(KEY);
  } catch {
    /* storage disabled: the tab follows the most recent login */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENT, { detail: slot }));
}

/** `url` with this tab's `?slot=` (for <img>/<video>/<a download>/EventSource URLs). */
export function withSlot(url: string, slot: string | null = getSlot()): string {
  if (!slot) return url;
  const i = url.indexOf("#");
  const [path, hash] = i < 0 ? [url, ""] : [url.slice(0, i), url.slice(i)];
  return `${path}${path.includes("?") ? "&" : "?"}slot=${encodeURIComponent(slot)}${hash}`;
}

export type FileQuery = Record<string, string | number | boolean | null | undefined>;

/** The one helper for same-origin API files opened as plain links (downloads, CSV, Markdown, JSON, media): `path`
 * plus `query` (null / undefined / "" dropped) plus this tab's `?slot=`, so the file is fetched as this tab's
 * identity. */
export function apiFileUrl(path: string, query: FileQuery = {}, slot: string | null = getSlot()): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  const s = q.toString();
  return withSlot(s ? `${path}${path.includes("?") ? "&" : "?"}${s}` : path, slot);
}

/** The CSRF cookie of a slot (the bare vp_csrf belongs to the most recent login). */
export function csrfCookieName(slot: string | null): string {
  return slot && slot !== LEGACY_SLOT ? `vp_csrf_${slot}` : "vp_csrf";
}
